import { AxiosError } from 'axios';
import axiosClient from '~/apis/axiosClient';
import i18n from '~/utils/i18n';
import type {
  IApiCallResponse,
  ICallAcceptResult,
  ICallEndResult,
  ICallInitiateResult,
  ICallStatusResult,
} from '~/utils/interface/call';

const BASE = '/api/calls';
const CALL_NS = 'admin/call';

function callApiMsg(key: string): string {
  return i18n.t(key, { ns: CALL_NS });
}

export class CallApiError extends Error {
  readonly messageCode?: string;
  readonly httpStatus?: number;

  constructor(message: string, messageCode?: string, httpStatus?: number) {
    super(message);
    this.name = 'CallApiError';
    this.messageCode = messageCode;
    this.httpStatus = httpStatus;
  }
}

function unwrap<T>(response: { data: IApiCallResponse<T> }, fallbackMessage: string): T {
  const body = response.data;
  if (!body?.result) {
    throw new CallApiError(body?.message || fallbackMessage, body?.message_code, body?.status);
  }
  return body.result;
}

export class CallNetworkError extends Error {
  readonly isNetworkError = true;

  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = 'CallNetworkError';
  }
}

function mapAxiosError(err: unknown, fallback: string): Error {
  if (err instanceof AxiosError) {
    if (!err.response) {
      const code = err.code ?? '';
      const message = err.message || fallback;
      return new CallNetworkError(message, code);
    }
    const status = err.response?.status;
    const data = err.response?.data as IApiCallResponse<unknown>;
    const message = data?.message;
    const messageCode = data?.message_code;
    if (status === 404) {
      return new CallApiError(message || callApiMsg('api.notFound'), messageCode, status);
    }
    return new CallApiError(message || fallback, messageCode, status);
  }
  if (err instanceof Error) return err;
  return new Error(fallback);
}

export type CallInitiateResponse = ICallInitiateResult;
export type CallStatusResponse = ICallStatusResult;

export const callAPI = {
  async initiateCall(target_username: string): Promise<ICallInitiateResult> {
    const fallback = callApiMsg('api.initiateFailed');
    try {
      const res = await axiosClient.post<IApiCallResponse<ICallInitiateResult>>(`${BASE}/initiate`, {
        target_username,
      });
      return unwrap(res, fallback);
    } catch (err) {
      throw mapAxiosError(err, fallback);
    }
  },

  async getCallStatus(callId: string): Promise<ICallStatusResult> {
    const fallback = callApiMsg('api.statusFailed');
    try {
      const res = await axiosClient.get<IApiCallResponse<ICallStatusResult>>(`${BASE}/${callId}/status`);
      return unwrap(res, fallback);
    } catch (err) {
      throw mapAxiosError(err, fallback);
    }
  },

  async acceptCall(callId: string): Promise<ICallAcceptResult> {
    const fallback = callApiMsg('api.acceptFailed');
    try {
      const res = await axiosClient.post<IApiCallResponse<ICallAcceptResult>>(`${BASE}/${callId}/accept`);
      return unwrap(res, fallback);
    } catch (err) {
      throw mapAxiosError(err, fallback);
    }
  },

  async rejectCall(callId: string): Promise<void> {
    const fallback = callApiMsg('api.rejectFailed');
    try {
      await axiosClient.post<IApiCallResponse<{ call_id: string; status: string }>>(`${BASE}/${callId}/reject`);
    } catch (err) {
      throw mapAxiosError(err, fallback);
    }
  },

  async endCall(callId: string): Promise<ICallEndResult> {
    const fallback = callApiMsg('api.endFailed');
    try {
      const res = await axiosClient.post<IApiCallResponse<ICallEndResult>>(`${BASE}/${callId}/end`);
      return unwrap(res, fallback);
    } catch (err) {
      throw mapAxiosError(err, fallback);
    }
  },
};

export default callAPI;
